// src/com/veh_info/VehInfoListForSms.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import useClientSize from '../hook/useClientSize';
import { Button, IconButton } from '@mui/material';
import VehFilter from '../common/VehFilter/VehFilter';
import PostAddIcon from '@mui/icons-material/PostAdd';
import MobileFriendlyIcon from '@mui/icons-material/MobileFriendly';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import FlexyTable from '../common/flexytable/FlexyTable';
import { AppObject, AppPalette, AppWord, TableColumnType } from '../../model/AppConst';
import styled from '@emotion/styled';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { LIST_VEH_INFO } from './VehInfoGql';
import ResponseAlert from '../message/ResponseAlert';
import { vehInfoListRepo } from '../../model/CvoModel';
import { ContentHeader, HeaderLeft, HeaderRight } from '../common/CvoStyled';
import SearchBox from '../common/SearchBox';
import { useSnackbar } from 'notistack';

const VehInfoListContainer = styled(Box)({
    flexGrow:1, display:'flex', flexDirection:'column'
});

const ToolEventToggleUseYn = 'toggleUseYn';
const TableColumns = [
    //{ id: 'veh_id', label: 'veh_id', minWidth: 100, show: true, align: 'inherit', type:TableColumnType.INTEGER },
    //{ id: 'cust_id', label: 'cust_id', minWidth: 100, show: true, align: 'inherit', type:TableColumnType.TEXT },
	{ id: 'vehPlates', label: '차량번호', width: 120, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
	{ id: 'vehAlias', label: '지도표시명', width: 100, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
	{ id: 'driverName', label: '기사', width: 100, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
	{ id: 'driverMobile', label: '휴대전화', width: 100, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
	{ id: 'gpsTime', label: '최종보고', width: 180, show: true, option:{align: 'inherit', type: TableColumnType.DATETIME} },
    { id: 'useYn', label: '사용여부', width: 100, show: true, option:{
        align: 'center', type:TableColumnType.CHAR, ynBoolean: ['사용 중','사용중지'],
        toggle: ToolEventToggleUseYn
    } },
	{ id: 'distKm', label: '누적거리', width: 100, show: true, option:{align: 'right', type: TableColumnType.INTEGER} },
    { id: 'vehTypeTon', label: '차종-톤수', width: 100, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
	{ id: 'termId', label: '단말기번호', width: 100, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    //{ id: 'vehAddr', label: '차고지', width: 100, show: false, option:{align: 'inherit', type:TableColumnType.TEXT} },
    { id: 'custName', label: '운송사', width: 100, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} }, // VEH_AND_TERM
    /* 청소업체 운행통계에 이 코드 사용.
    { id: 'veh01_cd', label: 'veh01_cd', minWidth: 100, show: true, align: 'inherit', type:TableColumnType.INTEGER },
    { id: 'veh02_cd', label: 'veh02_cd', minWidth: 100, show: true, align: 'inherit', type:TableColumnType.INTEGER },
    { id: 'veh03_cd', label: 'veh03_cd', minWidth: 100, show: true, align: 'inherit', type:TableColumnType.INTEGER }*/
];

const MinSecReloadInterval = 5000;

export default function VehInfoListForSms({
    onRequestAddToReceiver,
    onRequestAddAllToReceiver
}) {
    const vehInfoList = useReactiveVar(vehInfoListRepo);
    const [filterBoxState, setFilterBoxState] = useState({open:false});
    //const [vehFilterCriteria, setVehFilterCriteria] = useState(null);
    const [vehInfoListFilter, setVehInfoListFilter] = useState({criteria:null, filterData: null});
    const [search, setSearch] = useState('');
    const clientSize = useClientSize();
    const [responseAlert, setResponseAlert] = useState(null);
    const { enqueueSnackbar } = useSnackbar();
    const reloadTimeRef = useRef(0);
    const [getVehInfoList, responseList] = useLazyQuery(LIST_VEH_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
        onError: (error) => {
            setResponseAlert({open:true, error: error, title: "차량목록 가져오기 실패."})
        }
    });

    useEffect(() => {
        getVehInfoList();
    }, []);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.vehInfoList) {
            reloadTimeRef.current = (new Date()).getTime();
            vehInfoListRepo(data.vehInfoList);
        }
    };

    const onChangeSearch = (text) => {
        setSearch(text);
    };

    const onRequestReload = () => {
        const diff = (new Date()).getTime() - reloadTimeRef.current;
        if(diff > MinSecReloadInterval) {
            getVehInfoList();
        }
        else {
            enqueueSnackbar(AppWord.NO_RELOAD_IN_5_SEC,
                {variant:AppPalette.WarnColor, autoHideDuration:1500});
        }
    };

    const onClickAddAll = () => {
        const all = vehInfoList.filter(v => v.useYn==='Y' && v.driverMobile && v.driverMobile.length >= 10).map(v => ({tel: v.driverMobile, name: v.driverName}));
        if(all.length > 0) onRequestAddAllToReceiver(all);
    };

    const onClickAdd = (row) => {
        if(row.driverMobile && row.driverMobile.length >= 10) {
            onRequestAddToReceiver({tel: row.driverMobile, name: row.driverName});
        }
        else {
            enqueueSnackbar(row.vehPlates + ' 운전자의 휴대전화번호가 올바르지 않습니다.',
                {variant:AppPalette.ErrorColor, autoHideDuration:1500});
        }
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppWord.CMD_ADD: onClickAdd(row); break;
            default: return;
        }
    };

    const onCloseFilterBox = () => {setFilterBoxState({open:false});};

    const onClickSearchFilterButton = (event) => {
        setFilterBoxState({
            open:true, 
            top: clientSize.HeaderHeight 
                + clientSize.dataContainerPadding 
                + clientSize.SmallBoxPadding,
            left: event.clientX - 10});
    };

    // filter test:  vehTonId: 1 + 3
    //const criteria = {vehTonId: [1, 3]};
    const onCompleteFilterBox = (criteria, newFilterData) => {
        //setVehFilterCriteria(criteria);
        //setFilterBoxState({open:false});
        const newState = {...filterBoxState};
        newState.open = false;
        setFilterBoxState(newState);
        setVehInfoListFilter({criteria:criteria, filterData: newFilterData});
    };
    
    const onClickClearFilter = () => {
        setVehInfoListFilter({criteria:null, filterData: null});
    };

    return(
        <VehInfoListContainer>
            <ContentHeader>
                <HeaderLeft>
                    <SearchBox onChange={onChangeSearch} value={search} />
                    <IconButton size='small' onClick={onClickSearchFilterButton}>
                        <FilterAltIcon />
                    </IconButton>
                    {
                        vehInfoListFilter.criteria
                        &&
                        <IconButton size={AppWord.SMALL}
                            color="secondary"
                            onClick={onClickClearFilter}
                        >
                            <FilterAltOffIcon />
                        </IconButton>
                    }
                </HeaderLeft>

                <HeaderRight>
                    <Button
                        startIcon={<PostAddIcon />}
                        variant='contained'
                        onClick={onClickAddAll}
                        color={AppPalette.InheritColor}
                    >
                        모두 추가
                    </Button>
                </HeaderRight>
            </ContentHeader>
            <FlexyTable
                name="veh_info_list_for_sms"
                uniqueKey="vehId"
                columns={TableColumns}
                records={vehInfoList}
                search={search}
                criteria={vehInfoListFilter.criteria}
                tools={[
                    {id:AppWord.CMD_ADD, icon:<MobileFriendlyIcon fontSize='small' />},
                ]}
                onClickTool={onClickToolOnRow}
                onRequestReload={onRequestReload}
                onClickOneRow={onClickAdd}
                defaultSorter={{id:'vehPlates', type: TableColumnType.TEXT}}
            />
            <VehFilter open={filterBoxState.open}
                top={filterBoxState.top}
                left={filterBoxState.left}
                useCustAgree={true}
                onComplete={onCompleteFilterBox}
                onCloseBox={onCloseFilterBox}
                filterDataNow={vehInfoListFilter.filterData}
            />
            <ResponseAlert open={responseAlert ? responseAlert.open : false}
                alertData={responseAlert}
                onClose={() => {setResponseAlert(null)}}/>
        </VehInfoListContainer>
    );

}
